/**
 * Stage 2 orchestrator — converts DocumentIR to ProseMirror JSON.
 *
 * This is the entry point for Stage 2. It:
 * 1. Converts each BlockIR to a PMNode
 * 2. Runs post-processing passes (page breaks, math blocks, images, lists)
 * 3. Returns the final OoxmlParseResult
 */

import type { PMNode, PMDoc, OoxmlParseResult } from "../../ooxmlParser/types";
import type { DocumentIR, BlockIR } from "../irTypes";
import type { ParseContext } from "../../ooxmlParser/types";
import type { DocxPackage } from "../../ooxmlParser/zipReader";
import { getChildNS, W_NS } from "../../ooxmlParser/zipReader";
import { convertParagraphIR } from "./paragraphConverter";
import { groupListNodes } from "../../ooxmlParser/listParser";
import { parseHeadersAndFooters } from "../../ooxmlParser/headerFooterParser";
import { parseBodyContent } from "../stage1/buildDocumentIR";
import { resolveImages } from "./imageResolver";
import { docxNoteId } from "./runConverter";
import { parseSectPrPageSetup } from "../../pageSetup";

/**
 * Convert a DocumentIR to ProseMirror JSON output.
 */
export async function irToProseMirror(
  ir: DocumentIR,
  ctx: ParseContext,
  pkg: DocxPackage
): Promise<OoxmlParseResult> {
  const generateNodeId = ctx.generateNodeId;

  // Convert blocks to PMNodes
  let content: PMNode[] = [];
  for (const block of ir.blocks) {
    const node = convertBlockIR(block, generateNodeId);
    if (node) content.push(node);
  }

  // Post-processing passes (matching V1's pipeline)

  // 1. Resolve page breaks
  content = resolvePageBreaks(content);

  // 2. Resolve math blocks
  content = resolveMathBlocks(content);

  // 3. Resolve images (async) — reuses V1's parseDrawing/parsePict
  content = await resolveImages(content, ctx, pkg.zip);

  // 4. Group lists (reuses V1's list grouping — operates on PMNodes)
  content = groupListNodes(content, ctx);

  // 5. Resolve footnotes/endnotes — parse definitions from footnotes.xml /
  //    endnotes.xml and append footnotesSection nodes for referenced notes
  content = await resolveFootnoteSections(content, ctx, pkg, ir.messages);

  // Ensure document has at least one paragraph
  if (content.length === 0) {
    content.push({
      type: "paragraph",
      attrs: { id: generateNodeId() },
    });
  }

  const doc: PMDoc = { type: "doc", content };

  // 6. Parse headers and footers from the body-level sectPr (reuses V1's
  //    parser — this was missing in the first V2 cut, which silently broke
  //    header/footer import when V2 became the active pipeline)
  const bodyEls = pkg.documentXml.getElementsByTagNameNS(W_NS, "body");
  const bodyEl = bodyEls.length > 0 ? bodyEls[0] : null;
  const sectPrEl = bodyEl ? getChildNS(bodyEl, W_NS, "sectPr") : null;
  const headerFooter = await parseHeadersAndFooters(
    pkg,
    ctx,
    sectPrEl,
    ir.messages
  );

  // Page size / orientation / margins from the same sectPr
  const pageSetup = parseSectPrPageSetup(sectPrEl);

  // Extract V1-delegated comment/track change results if present
  const irAny = ir as any;
  const comments = irAny._v1Comments || [];
  const trackChanges = irAny._v1TrackChanges || [];
  const hasTrackChanges = irAny._v1HasTrackChanges || trackChanges.length > 0;

  return {
    doc,
    messages: ir.messages,
    hasTrackChanges,
    comments,
    trackChanges,
    headerFooter,
    pageSetup,
  };
}

/**
 * Parse footnotes.xml / endnotes.xml definitions and append a
 * footnotesSection node per kind, containing the referenced notes in
 * reference order. Separator pseudo-notes (w:type="separator" /
 * "continuationSeparator") are skipped.
 */
async function resolveFootnoteSections(
  content: PMNode[],
  ctx: ParseContext,
  pkg: DocxPackage,
  messages: string[]
): Promise<PMNode[]> {
  // Collect refs in document order (recursing into tables/cells)
  const refOrder: { footnoteId: string; kind: "footnote" | "endnote" }[] = [];
  const walk = (nodes: PMNode[] | undefined) => {
    if (!nodes) return;
    for (const node of nodes) {
      if (node.type === "footnoteRef" && node.attrs?.footnoteId) {
        refOrder.push({
          footnoteId: node.attrs.footnoteId as string,
          kind: (node.attrs.kind as "footnote" | "endnote") || "footnote",
        });
      }
      if (node.content) walk(node.content);
    }
  };
  walk(content);
  if (refOrder.length === 0) return content;

  const emptyParagraph = (): PMNode => ({
    type: "paragraph",
    attrs: { id: ctx.generateNodeId() },
  });

  // Parse note definitions: footnoteId → block nodes
  const definitions = new Map<string, PMNode[]>();
  const parseNotesXml = async (
    xml: Document | null,
    noteKind: "footnote" | "endnote"
  ) => {
    if (!xml) return;
    const tag = noteKind === "endnote" ? "endnote" : "footnote";
    const els = xml.getElementsByTagNameNS(W_NS, tag);
    for (let i = 0; i < els.length; i++) {
      const el = els[i];
      const type =
        el.getAttributeNS(W_NS, "type") || el.getAttribute("w:type") || "";
      // Separator pseudo-notes define the rule line above the notes area
      if (type === "separator" || type === "continuationSeparator") continue;
      const id = el.getAttributeNS(W_NS, "id") || el.getAttribute("w:id") || "";
      if (!id) continue;

      const blockIRs = parseBodyContent(el, ctx, messages);
      let blocks: PMNode[] = [];
      for (const blockIR of blockIRs) {
        const node = convertBlockIR(blockIR, ctx.generateNodeId);
        if (node) blocks.push(node);
      }
      blocks = resolveMathBlocks(blocks);
      blocks = await resolveImages(blocks, ctx, pkg.zip);
      blocks = groupListNodes(blocks, ctx);
      // Page breaks make no sense inside a note
      blocks = blocks.filter(n => n.type !== "pageBreak");
      if (blocks.length === 0) blocks = [emptyParagraph()];
      definitions.set(docxNoteId(noteKind, id), blocks);
    }
  };

  const kindsNeeded = new Set(refOrder.map(r => r.kind));
  if (kindsNeeded.has("footnote"))
    await parseNotesXml(pkg.footnotesXml, "footnote");
  if (kindsNeeded.has("endnote"))
    await parseNotesXml(pkg.endnotesXml, "endnote");

  // Build one section per kind, items in reference order
  const buildSection = (kind: "footnote" | "endnote"): PMNode | null => {
    const seen = new Set<string>();
    const items: PMNode[] = [];
    for (const ref of refOrder) {
      if (ref.kind !== kind || seen.has(ref.footnoteId)) continue;
      seen.add(ref.footnoteId);
      const blocks = definitions.get(ref.footnoteId);
      if (!blocks) {
        messages.push(
          `Warning: no definition found for ${kind} reference ${ref.footnoteId}`
        );
      }
      items.push({
        type: "footnoteItem",
        attrs: { footnoteId: ref.footnoteId, id: ctx.generateNodeId() },
        content: blocks || [emptyParagraph()],
      });
    }
    if (items.length === 0) return null;
    return {
      type: "footnotesSection",
      attrs: { kind },
      content: items,
    };
  };

  const out = [...content];
  const footnoteSection = buildSection("footnote");
  const endnoteSection = buildSection("endnote");
  if (footnoteSection) out.push(footnoteSection);
  if (endnoteSection) out.push(endnoteSection);
  if (footnoteSection || endnoteSection) {
    messages.push(
      `Imported ${footnoteSection?.content?.length || 0} footnote(s), ${endnoteSection?.content?.length || 0} endnote(s)`
    );
  }
  return out;
}

/**
 * Convert a single BlockIR to a PMNode.
 */
function convertBlockIR(
  block: BlockIR,
  generateNodeId: () => string
): PMNode | null {
  switch (block.kind) {
    case "paragraph":
      return convertParagraphIR(block, generateNodeId);
    case "table":
      // Phase 3 — table converter (not yet implemented)
      return null;
    case "horizontalRule":
      return { type: "horizontalRule" };
    case "passthrough":
      // Pre-built PMNode from V1 delegation
      return block.pmNode;
    default:
      return null;
  }
}

/**
 * Resolve page breaks — split paragraphs containing inline page breaks,
 * drop empty paragraphs with section breaks.
 * (Port of V1's resolvePageBreaks)
 */
function resolvePageBreaks(nodes: PMNode[]): PMNode[] {
  const result: PMNode[] = [];

  for (const node of nodes) {
    // Recurse into tables
    if (node.type === "table" && node.content) {
      node.content = node.content.map(row => {
        if (row.content) {
          row.content = row.content.map(cell => {
            if (cell.content) {
              cell.content = resolvePageBreaks(cell.content);
            }
            return cell;
          });
        }
        return row;
      });
      result.push(node);
      continue;
    }

    // Check for section break only paragraphs
    if (
      node.attrs?._hasSectBreak &&
      (!node.content || node.content.length === 0)
    ) {
      // Drop empty section break paragraphs
      continue;
    }

    // Check if paragraph contains page breaks
    if (node.content && node.content.some(c => c.type === "pageBreak")) {
      // Split on page breaks
      let currentContent: PMNode[] = [];
      for (const child of node.content) {
        if (child.type === "pageBreak") {
          // Emit current content as paragraph (if non-empty)
          if (currentContent.length > 0) {
            result.push({ ...node, content: currentContent });
            currentContent = [];
          }
          // Emit page break as standalone node
          result.push({ type: "pageBreak" });
        } else {
          currentContent.push(child);
        }
      }
      // Emit remaining content
      if (currentContent.length > 0) {
        result.push({ ...node, content: currentContent });
      }
    } else {
      result.push(node);
    }
  }

  return result;
}

/**
 * Resolve math blocks — convert _mathBlock placeholders to block-level blockMath nodes,
 * splitting the parent paragraph if needed.
 * (Port of V1's resolveMathBlocks)
 */
function resolveMathBlocks(nodes: PMNode[]): PMNode[] {
  const result: PMNode[] = [];

  for (const node of nodes) {
    // Recurse into tables
    if (node.type === "table" && node.content) {
      node.content = node.content.map(row => {
        if (row.content) {
          row.content = row.content.map(cell => {
            if (cell.content) {
              cell.content = resolveMathBlocks(cell.content);
            }
            return cell;
          });
        }
        return row;
      });
      result.push(node);
      continue;
    }

    // Check if paragraph contains math block placeholders
    if (node.content && node.content.some(c => c.type === "_mathBlock")) {
      let currentContent: PMNode[] = [];
      for (const child of node.content) {
        if (child.type === "_mathBlock") {
          // Emit current content as paragraph (if non-empty)
          if (currentContent.length > 0) {
            result.push({ ...node, content: currentContent });
            currentContent = [];
          }
          // Emit block math node
          result.push({
            type: "blockMath",
            attrs: { latex: child.attrs?.latex || "" },
          });
        } else {
          currentContent.push(child);
        }
      }
      // Emit remaining content
      if (currentContent.length > 0) {
        result.push({ ...node, content: currentContent });
      }
    } else {
      result.push(node);
    }
  }

  return result;
}
